import { z } from "zod";
import { PricingLineSchema } from "./items";

// The JSON Schema that defines what inputs a price model accepts
export const PriceModelInputSchemaDefinition = z.record(z.string(), z.any());

/** Allowed JSON Schema types for price model input properties (primitives and one-level arrays). */
export const PRICE_MODEL_ALLOWED_PROPERTY_TYPES: ReadonlySet<string> = new Set([
  "string",
  "number",
  "integer",
  "boolean",
  "array",
]);

// Core Price Model schema (API response type)
export const PriceModelSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  script: z.string(),
  inputSchema: PriceModelInputSchemaDefinition,
  defaultInputs: z.record(z.string(), z.any()),
  version: z.number().int(),
  archivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type PriceModel = z.infer<typeof PriceModelSchema>;

export const CreatePriceModelParamsSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  script: z.string(),
  inputSchema: PriceModelInputSchemaDefinition,
  defaultInputs: z.record(z.string(), z.any()).optional(),
});
export type CreatePriceModelParams = z.infer<
  typeof CreatePriceModelParamsSchema
>;

export const UpdatePriceModelParamsSchema = z.object({
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  script: z.string().optional(),
  inputSchema: PriceModelInputSchemaDefinition.optional(),
  defaultInputs: z.record(z.string(), z.any()).optional(),
  archived: z.boolean().optional(),
});
export type UpdatePriceModelParams = z.infer<
  typeof UpdatePriceModelParamsSchema
>;

// Script execution input/output
export const ExecutePriceModelParamsSchema = z.object({
  inputs: z.record(z.string(), z.any()),
  quantities: z.array(z.number().positive()),
});
export type ExecutePriceModelParams = z.infer<
  typeof ExecutePriceModelParamsSchema
>;

// Script output contract — what the Python script must print to stdout
export const PriceModelScriptOutputSchema = z.object({
  name: z.string().optional(),
  operations: z.array(
    z.object({
      operationName: z.string(),
      overrideOperationCost: z.string().optional(),
      overrideSetupRate: z.string().optional(),
      setupTime: z.string().optional(),
      runTimePerItem: z.string().optional(),
      efficiency: z.string().optional(),
      minimumCharge: z.string().optional(),
      reasoning: z.string(),
      quantityBreaks: z
        .array(
          z.object({
            minQty: z.number().int().positive(),
            overrideOperationCost: z.string().optional(),
          }),
        )
        .optional(),
    }),
  ),
  components: z
    .array(
      z.object({
        itemName: z.string(),
        revision: z.string().optional(),
        quantity: z.string(),
        reasoning: z.string(),
      }),
    )
    .optional(),
  margin: z
    .array(
      z.object({
        type: z.enum([
          "purchased",
          "operations",
          "outsideOperations",
          "material",
          "all",
        ]),
        minQty: z.number().int().positive().optional(),
        marginAmount: z.union([
          z.object({
            type: z.literal("PERCENTAGE"),
            percentage: z.string(),
          }),
          z.object({ type: z.literal("AMOUNT"), amount: z.string() }),
        ]),
      }),
    )
    .optional(),
  yield: z
    .array(
      z.object({
        minQty: z.number().int().positive().optional(),
        percentage: z.string(),
      }),
    )
    .optional(),
  pricingLines: z.array(PricingLineSchema).optional(),
  calculations: z.record(z.string(), z.string()).optional(),
});
export type PriceModelScriptOutput = z.infer<
  typeof PriceModelScriptOutputSchema
>;

// API response wrappers
export const CreatePriceModelResponseSchema = z.object({
  priceModel: PriceModelSchema,
});
export const GetPriceModelResponseSchema = z.object({
  priceModel: PriceModelSchema,
});
export const UpdatePriceModelResponseSchema = z.object({
  priceModel: PriceModelSchema,
});
export const ListPriceModelsResponseSchema = z.object({
  priceModels: z.array(PriceModelSchema),
});
export const DeletePriceModelResponseSchema = z.object({
  success: z.boolean(),
});

// Execution response includes the resolved item structure
export const ExecutePriceModelResponseSchema = z.object({
  output: PriceModelScriptOutputSchema,
  resolvedOperations: z.array(
    z.object({
      operationId: z.string(),
      operationName: z.string(),
      overrideOperationCost: z.string().optional(),
      overrideSetupRate: z.string().optional(),
      setupTime: z.string().optional(),
      runTimePerItem: z.string().optional(),
      efficiency: z.string().optional(),
      minimumCharge: z.string().optional(),
      reasoning: z.string(),
      quantityBreaks: z
        .array(
          z.object({
            minQty: z.number().int().positive(),
            overrideOperationCost: z.string().optional(),
          }),
        )
        .optional(),
    }),
  ),
  resolvedComponents: z
    .array(
      z.object({
        itemId: z.string(),
        itemName: z.string(),
        revision: z.string().optional(),
        quantity: z.string(),
        reasoning: z.string(),
      }),
    )
    .optional(),
});
export type ExecutePriceModelResponse = z.infer<
  typeof ExecutePriceModelResponseSchema
>;
